'use client';

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import {
  resendSelfOnboardOtp,
  verifySelfOnboardOtp,
  SelfOnboardApiError,
} from '@/services/self-onboard.service';
import {
  readSelfOnboardSession,
  writeSelfOnboardSetup,
} from '@/lib/public-routing';

const OTP_TTL_SECONDS = 120;

export default function SelfOnboardVerifyOtpPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const session = readSelfOnboardSession();
    if (!session || session.token !== token) {
      navigate(`/self-onboard/${token}/details`, { replace: true });
      return;
    }
    setSessionId(session.sessionId);
    setMaskedEmail(session.maskedEmail ?? 'your work email');
    setResendCooldown(OTP_TTL_SECONDS);
  }, [navigate, token]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleVerify = async () => {
    if (otp.length !== 6 || !sessionId) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await verifySelfOnboardOtp(sessionId, otp);
      writeSelfOnboardSetup({ setupToken: result.setupToken, sessionId });
      navigate(`/self-onboard/${token}/set-password`);
    } catch (err) {
      setError(err instanceof SelfOnboardApiError ? err.message : 'Verification failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!sessionId || resendCooldown > 0) return;
    setError('');
    try {
      await resendSelfOnboardOtp(sessionId);
      setResendCooldown(OTP_TTL_SECONDS);
      setOtp('');
    } catch (err) {
      setError(err instanceof SelfOnboardApiError ? err.message : 'Could not resend code.');
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8">
      <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Verify your email</h1>
      <p className="text-sm text-slate-500 mb-6">
        We sent a 6-digit code to <strong>{maskedEmail}</strong>
      </p>

      <div className="flex justify-center mb-6">
        <InputOTP maxLength={6} value={otp} onChange={setOtp}>
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {error ? <p className="text-sm text-red-600 mb-4 text-center">{error}</p> : null}

      <button
        type="button"
        disabled={submitting || otp.length !== 6}
        onClick={() => void handleVerify()}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] text-white font-semibold disabled:opacity-60"
      >
        {submitting ? 'Verifying…' : 'Verify code'}
      </button>

      <button
        type="button"
        disabled={resendCooldown > 0}
        onClick={() => void handleResend()}
        className="w-full mt-3 py-2 text-sm text-[var(--hrm-primary-dark)] disabled:text-slate-400"
      >
        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
      </button>

      <Link
        to={`/self-onboard/${token}/details`}
        className="mt-4 inline-block text-sm text-slate-500 hover:text-slate-700"
      >
        ← Back
      </Link>
    </div>
  );
}
